import { Container, Title, Text, Table, ThemeIcon, Paper, Group, rem, Stack, Badge, Grid, Avatar, Box } from '@mantine/core';
import { IconCheck, IconX, IconCrown, IconStarFilled } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { HOME_CONTENT } from '../../data/homeContent';

const MotionPaper = motion(Paper);
const MotionGridCol = motion.create(Grid.Col);

/**
 * Comparison component provides a side-by-side comparison of the tool's features against competitors,
 * and displays user reviews to build trust.
 */
export default function Comparison() {
  const { comparison } = HOME_CONTENT;
  return (
    <Container size="lg" py={{ base: 80, md: 120 }} id="comparison">

        <Grid gutter={60} align="start">

            {/* LEFT COLUMN: Comparison Table */}
            <MotionGridCol
                span={{ base: 12, lg: 7 }}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
            >
                <Stack gap="sm" mb="xl">
                    <Group>
                        <Badge variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }} size="md" radius="sm">{comparison.badge}</Badge>
                    </Group>
                    <Title order={2} fw={900} style={{ letterSpacing: '-1px', fontSize: '2.5rem', lineHeight: 1.1 }}>
                        {comparison.title} <span style={{ color: '#228be6' }}>{comparison.titleHighlight}</span>
                    </Title>
                    <Text c="dimmed" size="lg" maw={500}>
                        {comparison.description}
                    </Text>
                </Stack>

                <Paper
                    shadow="xl"
                    radius="lg"
                    withBorder
                    style={{
                        overflow: 'hidden',
                        backgroundColor: 'rgba(10, 10, 10, 0.6)',
                        borderColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)'
                    }}
                >
                    <Table verticalSpacing="md" horizontalSpacing="lg" withRowBorders={true}>
                        <Table.Thead>
                            <Table.Tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                <Table.Th style={{ width: '30%', color: 'var(--mantine-color-dimmed)' }}>Feature</Table.Th>
                                <Table.Th style={{ width: '35%', backgroundColor: 'rgba(34, 139, 230, 0.08)', borderTop: '2px solid #228be6' }}>
                                    <Group gap="xs">
                                        <ThemeIcon variant="transparent" color="blue">
                                            <IconCrown style={{ width: rem(20) }} />
                                        </ThemeIcon>
                                        <Text fw={800} size="md" c="blue.3">{comparison.ourName}</Text>
                                    </Group>
                                </Table.Th>
                                <Table.Th style={{ width: '35%' }}>
                                    <Text fw={600} c="dimmed" size="sm">Generic Scripts</Text>
                                </Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {comparison.rows.map((row) => (
                                <Table.Tr key={row.feature} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                    <Table.Td><Text fw={600} size="sm" c="white">{row.feature}</Text></Table.Td>

                                    {/* WINNER COLUMN */}
                                    <Table.Td style={{ backgroundColor: 'rgba(34, 139, 230, 0.03)' }}>
                                        <Group gap="xs">
                                            <ThemeIcon color="teal" variant="light" size="sm" radius="xl">
                                                <IconCheck style={{ width: rem(12) }} stroke={3} />
                                            </ThemeIcon>
                                            <Text size="sm" fw={700} c="white">{row.us}</Text>
                                        </Group>
                                    </Table.Td>

                                    {/* LOSER COLUMN */}
                                    <Table.Td>
                                        <Group gap="xs">
                                            <ThemeIcon color="red" variant="transparent" size="sm">
                                                <IconX style={{ width: rem(14) }} />
                                            </ThemeIcon>
                                            <Text size="sm" c="dimmed" td="line-through" style={{ opacity: 0.6 }}>{row.them}</Text>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Paper>
            </MotionGridCol>

            {/* RIGHT COLUMN: Domination Feed */}
            <MotionGridCol
                span={{ base: 12, lg: 5 }}
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.2 }}
            >
                <Stack gap="md" h="100%" justify="center">
                    <Group justify="space-between" align="center" mb="xs" px="xs">
                        <Title order={3} size="h3" fw={800}>{comparison.testimonials.title}</Title>
                    </Group>

                    {comparison.testimonials.items.map((t, i) => (
                        <MotionPaper
                            key={i}
                            p="md"
                            radius="md"
                            withBorder
                            style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                borderColor: 'rgba(255, 255, 255, 0.05)',
                                backdropFilter: 'blur(5px)'
                            }}
                            whileHover={{ y: -4, backgroundColor: 'rgba(255, 255, 255, 0.06)', borderColor: 'rgba(255, 255, 255, 0.1)' }}
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            viewport={{ once: true }}
                        >
                            <Group align="flex-start" wrap="nowrap">
                                <Avatar color={t.color} radius="xl" size="md">{t.name.substring(0,2)}</Avatar>
                                <Box style={{ flex: 1 }}>
                                    <Group gap="xs" mb={2} justify="space-between">
                                        <Group gap="xs">
                                            <Text fw={700} size="sm" c="white">{t.name}</Text>
                                            <ThemeIcon size="xs" variant="transparent" c="dimmed">
                                                <t.icon size={14} />
                                            </ThemeIcon>
                                        </Group>
                                        <Text size="xs" c="dimmed">{t.time}</Text>
                                    </Group>
                                    <Text size="sm" c="gray.4" lh={1.4}>
                                        "{t.msg}"
                                    </Text>
                                    <Group gap={2} mt="xs">
                                        {[1,2,3,4,5].map(s => <IconStarFilled key={s} size={12} color="#fab005" />)}
                                    </Group>
                                </Box>
                            </Group>
                        </MotionPaper>
                    ))}
                </Stack>
            </MotionGridCol>
        </Grid>

        {/* CSS for the pulsing dot on the LIVE badge */}
        <style>{`
            .pulse-badge .mantine-Badge-section[data-position="left"] {
                animation: pulse-dot 2s infinite;
            }
            @keyframes pulse-dot {
                0% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.4; transform: scale(0.8); }
                100% { opacity: 1; transform: scale(1); }
            }
        `}</style>
    </Container>
  );
}